/**
 * PrologueStageScene — the memory stages (owner: prologue-builder). One scene class (key
 * `MemoryStage`) registered for all five stage keys through `registerMemoryStages`; `init` picks the
 * StageDef by `stageKey`. The base MemoryStage plays the stage's intro timeline; `finish()` is
 * overridden to hand over to the stage controller (storm / roam / gaze / scripted) and only then
 * finishes for real. Owns a deterministic camera (follow / pan / hold + seeded shake + deck sway),
 * a HUD (hint, caption, meter, floating speech), stage input and the procedural set + effects.
 */
import Phaser from 'phaser';
import { Depth, FONT_BODY, FONT_TITLE, GAME_H, GAME_W } from '@/core/config';
import { FixedStepper } from '@/core/clock';
import { ctxOf } from '@/core/context';
import { bus, Events, type EventPayloads } from '@/core/events';
import { SeededRng } from '@/core/rng';
import { SceneKeys } from '@/core/scenes/keys';
import type { BattleOutcome, IActor } from '@/core/types/contracts';
import { MemoryStage, createSceneCinematicHost, type CinematicHost, type CinematicResult, type MemoryStageData } from '@/cinematics';
import { PlayerController, createActor, type Actor } from '@/characters';
import { runBattle } from '@/combat';
import { storyGate } from '@/interaction';
import { isStoryFlag } from '@/story/flags';
import { audio } from '@/audio';
import { getDialogueBox, uiRootOf } from '@/ui';
import { actorKeyOf } from '../actors';
import { PrologueEffects } from '../effects/PrologueEffects';
import { battleMark } from '../cinematics/stages';
import { STAGES, STORM_STAGE, isStageId, stageBlockedFn, type StageDef } from './defs';
import { buildStageSet, type StageSet } from './set';
import { createController, type StageContext, type StageController } from './controllers';

type Vec2 = { x: number; y: number };
type CamMode = 'follow' | 'pan' | 'hold';
interface PanState { from: Vec2; to: Vec2; elapsed: number; duration: number; resolve: () => void }
interface ShakeState { elapsed: number; duration: number; intensity: number }
interface Speech { text: Phaser.GameObjects.Text; actor: IActor; remaining: number }

const KC = Phaser.Input.Keyboard.KeyCodes;
const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export class PrologueStageScene extends MemoryStage {
  private def: StageDef = STORM_STAGE;
  private phase: 'intro' | 'main' | 'done' = 'intro';
  private controller: StageController | null = null;
  private set: StageSet | null = null;
  private fx: PrologueEffects | null = null;
  private playerActor: IActor | null = null;
  private pc: PlayerController | null = null;
  private rng = new SeededRng('stage');
  private stepper = new FixedStepper();
  private keys: Record<string, Phaser.Input.Keyboard.Key> = {};
  private readonly busOff: (() => void)[] = [];
  private readonly speeches: Speech[] = [];
  private readonly pendingTimers = new Set<() => void>();
  private hud: { hint: Phaser.GameObjects.Text; hintBg: Phaser.GameObjects.Rectangle; caption: Phaser.GameObjects.Text; captionBg: Phaser.GameObjects.Rectangle; meterLabel: Phaser.GameObjects.Text; meterBg: Phaser.GameObjects.Rectangle; meterFill: Phaser.GameObjects.Rectangle } | null = null;
  private camMode: CamMode = 'hold';
  private camHold: Vec2 = { x: 0, y: 0 };
  private pan: PanState | null = null;
  private shake: ShakeState | null = null;
  private shakeNow: Vec2 = { x: 0, y: 0 };
  private sway = 0;
  private swayT = 0;
  private inputLocked = false;
  private controlOn = false;
  private touchDir = 0;
  private touchHold = 0;
  private prevSkip: (() => boolean) | null | undefined;
  private mySkip: (() => boolean) | null = null;
  private dead = false;

  constructor() { super(SceneKeys.MEMORY_STAGE); }

  /** Verification probe: which part of the stage is running. */
  get stagePhase(): 'intro' | 'main' | 'done' { return this.phase; }
  get stageId(): string { return this.def.id; }

  // ------------------------------------------------------------------ lifecycle
  override init(data: MemoryStageData): void {
    super.init(data);
    const key = data.stageKey;
    this.def = isStageId(key) ? STAGES[key] : STORM_STAGE;
    if (!isStageId(key)) console.warn(`[prologue-stage] unknown stage "${key}"; showing the storm`);
    this.phase = 'intro';
    this.controller = null; this.set = null; this.fx = null; this.playerActor = null; this.pc = null; this.hud = null;
    this.stepper = new FixedStepper();
    this.keys = {};
    this.busOff.length = 0;
    this.speeches.length = 0;
    this.pendingTimers.clear();
    this.camMode = 'hold'; this.pan = null; this.shake = null; this.shakeNow = { x: 0, y: 0 }; this.sway = 0; this.swayT = 0;
    this.inputLocked = false; this.controlOn = false; this.touchDir = 0; this.touchHold = 0;
    this.prevSkip = undefined; this.mySkip = null;
    this.dead = false;
  }

  protected override buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setRoundPixels(true);
    cam.setBackgroundColor(this.def.bg);
    cam.setBounds(0, 0, this.def.width, this.def.height);
    this.add.rectangle(0, 0, GAME_W, GAME_H, this.def.tint, this.def.tintAlpha).setOrigin(0).setDepth(Depth.LIGHTING).setScrollFactor(0);
    try {
      const v = this.style.vignette;
      cam.filters.external.addVignette(0.5, 0.5, v.radius, v.strength, v.color);
    } catch (e) { console.warn('[prologue-stage] vignette filter unavailable', e); }
    // The stage label sits top-centre (outside the vignette's dark corners) on an opaque-enough plate
    // with a stroke, so it stays legible on the storm / sickroom stages; the hint plate hangs below it.
    const title = this.add.text(GAME_W / 2, 13, `回忆 · ${this.def.title}`, { fontFamily: FONT_TITLE, fontSize: '12px', color: '#fff1c0', stroke: '#0b0f14', strokeThickness: 3 }).setOrigin(0.5, 0).setDepth(Depth.UI).setScrollFactor(0);
    this.add.rectangle(GAME_W / 2, 11, Math.round(title.width) + 20, 20, 0x0b0f14, 0.78).setOrigin(0.5, 0).setDepth(Depth.UI - 1).setScrollFactor(0);
  }

  protected override buildStage(): void {
    const ctx = ctxOf(this);
    const seed = ctx?.seed ?? 1157;
    this.rng = new SeededRng(SeededRng.hash(`${seed}:stage:${this.def.id}`));
    this.fx = new PrologueEffects(this, { actors: () => this.actors, seed: SeededRng.hash(`${seed}:fx:${this.def.id}`) });
    this.set = buildStageSet(this, this.def, this.rng.fork('set'));

    const blocked = stageBlockedFn(this.def);
    const opts = { isBlocked: blocked, bounds: { w: this.def.width, h: this.def.height } };
    for (const c of this.def.cast) this.actors.set(c.id, createActor(this, c.id, actorKeyOf(c.id), c.x, c.y, c.facing, opts));
    const p = this.def.player;
    const player = createActor(this, p.id, actorKeyOf(p.id), p.x, p.y, p.facing, opts);
    this.actors.set(p.id, player);
    this.playerActor = player;
    if (this.def.kind === 'gaze') player.play('sit');
    if (this.def.kind === 'roam') {
      try { this.pc = new PlayerController(this, player as Actor, { mapId: `memory:${this.def.id}`, speed: 84 }); this.pc.setInputLocked(true); }
      catch (e) { console.warn('[prologue-stage] PlayerController unavailable', e); this.pc = null; }
    }
    this.camHold = this.clampScroll(player.x - GAME_W / 2, player.y - 16 - GAME_H / 2);
    this.camMode = this.def.kind === 'roam' ? 'follow' : 'hold';
    this.buildHud();
    this.wireStageInput();
    try {
      const a = audio();
      if (this.def.music) a.playMusic(this.def.music, 900);
      a.setAmbience((this.def.ambience ?? []).map((key) => ({ key, volume: 1 })), 900);
    } catch (e) { console.warn('[prologue-stage] audio failed', e); }
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
  }

  override create(): void {
    super.create();
    const ui = this.scene.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
    if (ui?.fade && ui.scene.isActive()) void ui.fade(false, 500);
  }

  protected override createHost(): CinematicHost {
    const runner = this.dialogue;
    if (!runner) throw new Error('[prologue-stage] dialogue runner missing');
    const state = this.stageData.state;
    return createSceneCinematicHost(this, {
      dialogue: runner,
      actors: this.actors,
      state,
      letter: this.stageData.letter,
      effects: this.fx,
      memory: () => { console.warn('[prologue-stage] nested launchMemory ignored'); return Promise.resolve('completed' as const); },
      battle: async (config: string): Promise<BattleOutcome> => {
        let outcome: BattleOutcome = 'goal';
        try { outcome = await runBattle(this, config); } catch (e) { console.warn('[prologue-stage] battle failed; treated as goal', e); }
        const mark = battleMark(config, outcome);
        if (state) state.setMark(mark); else ctxOf(this)?.story.mark(mark);
        return outcome;
      },
    });
  }

  protected override finish(result: CinematicResult): void {
    if (this.dead) { super.finish(result); return; }
    if (this.phase === 'intro') {
      this.phase = 'main';
      const controller = this.controller ?? createController(this.stageContext());
      this.controller = controller;
      this.installSkipHandler();
      if (result === 'skipped') controller.skip();
      controller.run().then(
        (r) => { this.phase = 'done'; this.restoreSkipHandler(); super.finish(result === 'skipped' || r === 'skipped' ? 'skipped' : 'completed'); },
        (e) => { console.error('[prologue-stage] controller failed', e); this.phase = 'done'; this.restoreSkipHandler(); super.finish(result); },
      );
      return;
    }
    if (this.phase === 'main') return; // the controller decides when the stage ends
    super.finish(result);
  }

  override update(_time: number, delta: number): void {
    if (this.dead) return;
    this.stepper.tick(delta, (dt) => {
      this.set?.step(dt);
      this.controller?.step(dt);
      this.stepSpeeches(dt);
      this.stepCamera(dt);
    });
  }

  private cleanup(): void {
    this.dead = true;
    this.restoreSkipHandler();
    for (const off of this.busOff.splice(0)) { try { off(); } catch { /* ignore */ } }
    const kb = this.input.keyboard;
    if (kb) { for (const k of Object.values(this.keys)) { try { kb.removeKey(k, true); } catch { /* ignore */ } } }
    this.keys = {};
    for (const done of [...this.pendingTimers]) done();
    this.pendingTimers.clear();
    if (this.pan) { const p = this.pan; this.pan = null; p.resolve(); }
    try { this.controller?.destroy(); } catch { /* ignore */ }
    this.controller = null;
    for (const s of this.speeches) { try { s.text.destroy(); } catch { /* ignore */ } }
    this.speeches.length = 0;
    try { this.pc?.destroy(); } catch { /* ignore */ }
    this.pc = null;
    try { this.set?.destroy(); } catch { /* ignore */ }
    this.set = null;
    try { this.fx?.destroy(); } catch { /* ignore */ }
    this.fx = null;
    this.hud = null;
    this.playerActor = null;
  }

  // ------------------------------------------------------------------ controller context
  private stageContext(): StageContext {
    const ctx = ctxOf(this);
    const state = this.stageData.state;
    const gate = state
      ? { hasFlag: (f: string) => isStoryFlag(f) && (ctx?.story.has(f) ?? false), hasMark: (m: string) => state.hasMark(m), setMark: (m: string) => state.setMark(m) }
      : storyGate(ctx.story, isStoryFlag);
    return {
      def: this.def,
      rng: this.rng.fork('controller'),
      actors: this.actors,
      playerActor: this.playerActor as IActor,
      fx: this.fx as PrologueEffects,
      gate,
      playTimeline: (id, fastForward) => this.playTimeline(id, fastForward),
      runDialogue: (id) => this.dialogue?.run(id) ?? Promise.resolve(),
      skipPlaying: () => this.skipPlaying(),
      isBusy: () => this.isBusy(),
      setPlayerControl: (on) => this.setPlayerControl(on),
      showHint: (text, ms) => this.showHint(text, ms),
      showCaption: (text, ms) => this.showCaption(text, ms),
      showSpeech: (actorId, text, ms) => this.showSpeech(actorId, text, ms),
      setMeter: (label, value) => this.setMeter(label, value),
      setSway: (lean) => { this.sway = lean; },
      cameraShake: (ms, intensity) => this.cameraShake(ms, intensity),
      sfx: (key) => { try { audio().sfx(key); } catch { /* silent */ } },
      confirmHeld: () => this.confirmHeld(),
      dirX: () => this.dirX(),
      wait: (ms) => this.wait(ms),
      isAlive: () => !this.dead,
    };
  }

  private playTimeline(id: string, fastForward = false): Promise<CinematicResult> {
    const player = this.player;
    if (!player || this.dead) return Promise.resolve('completed');
    return player.play(id, { fastForward });
  }

  private skipPlaying(): boolean {
    if (this.player?.isPlaying()) return this.player.skip();
    if (this.dialogue?.isRunning()) { this.dialogue.abort(); return true; }
    return false;
  }

  private isBusy(): boolean {
    return !!this.dialogue?.isRunning() || !!this.player?.isPlaying() || !!uiRootOf(this)?.hasModal();
  }

  // ------------------------------------------------------------------ verification hooks (window.__nantianPrologue)
  /** Exactly what one Enter press does on this stage (base advance + stage confirm). */
  debugConfirm(): void {
    if (this.dead) return;
    this.dialogue?.advance();
    this.onConfirmKey();
  }
  /** Exactly what one X press does on this stage (base timeline skip, then the controller). Returns true when something converged. */
  debugCancel(): boolean {
    if (this.dead) return false;
    const playing = !!this.player?.isPlaying();
    this.player?.skip();
    this.onCancel();
    return playing || this.phase === 'main';
  }
  /** True while a dialogue / timeline / modal owns the stage input. */
  debugBusy(): boolean { return this.isBusy(); }

  private wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.dead || ms <= 0) { resolve(); return; }
      const done = (): void => { this.pendingTimers.delete(done); resolve(); };
      this.pendingTimers.add(done);
      this.time.delayedCall(ms, done);
    });
  }

  // ------------------------------------------------------------------ input
  private wireStageInput(): void {
    const kb = this.input.keyboard;
    if (kb) {
      this.keys = kb.addKeys({ left: KC.LEFT, right: KC.RIGHT, a: KC.A, d: KC.D, enter: KC.ENTER, space: KC.SPACE, z: KC.Z }, false, false) as Record<string, Phaser.Input.Keyboard.Key>;
      const onCancel = (): void => this.onCancel();
      const onConfirm = (): void => this.onConfirmKey();
      kb.on('keydown-X', onCancel);
      kb.on('keydown-ESC', onCancel);
      kb.on('keydown-ENTER', onConfirm);
      kb.on('keydown-SPACE', onConfirm);
      kb.on('keydown-Z', onConfirm);
      // the base MemoryStage removes every keyboard listener on shutdown
    }
    const onBusConfirm = (p: EventPayloads[typeof Events.INPUT_CONFIRM]): void => { if (p.source === 'touch') this.onTouchConfirm(); };
    const onBusCancel = (p: EventPayloads[typeof Events.INPUT_CANCEL]): void => { if (p.source !== 'keyboard') this.onCancel(); };
    const onBusDir = (p: EventPayloads[typeof Events.INPUT_DIR]): void => { if (p.source === 'touch') this.touchDir = p.x; };
    bus.onTyped(Events.INPUT_CONFIRM, onBusConfirm);
    bus.onTyped(Events.INPUT_CANCEL, onBusCancel);
    bus.onTyped(Events.INPUT_DIR, onBusDir);
    this.busOff.push(() => bus.offTyped(Events.INPUT_CONFIRM, onBusConfirm), () => bus.offTyped(Events.INPUT_CANCEL, onBusCancel), () => bus.offTyped(Events.INPUT_DIR, onBusDir));
  }

  private onConfirmKey(): void {
    if (this.dead || uiRootOf(this)?.hasModal()) return;
    if (this.dialogue?.isRunning() || this.player?.isPlaying()) return; // the base advances the dialogue
    this.controller?.confirm();
  }
  private onTouchConfirm(): void {
    if (this.dead || uiRootOf(this)?.hasModal()) return;
    if (this.dialogue?.isRunning()) { this.dialogue.advance(); return; }
    if (this.player?.isPlaying()) return;
    this.touchHold = 0.6;
    this.controller?.confirm();
  }
  private onCancel(): void {
    if (this.dead || uiRootOf(this)?.hasModal()) return;
    if (this.player?.isPlaying()) { this.player.skip(); return; }
    if (this.phase === 'main') this.controller?.skip();
  }

  private installSkipHandler(): void {
    const ctx = ctxOf(this);
    if (!ctx || this.mySkip) return;
    this.prevSkip = ctx.skipHandler;
    this.mySkip = () => { if (this.player?.isPlaying()) return this.player.skip(); if (this.phase === 'main' && this.controller) { this.controller.skip(); return true; } return false; };
    ctx.skipHandler = this.mySkip;
  }
  private restoreSkipHandler(): void {
    const ctx = ctxOf(this);
    if (!ctx || !this.mySkip) return;
    if (ctx.skipHandler === this.mySkip) ctx.skipHandler = this.prevSkip ?? null;
    this.mySkip = null;
    this.prevSkip = undefined;
  }

  /** IWorldScene-style hook used by the cinematic host while a timeline plays. */
  setInputLocked(locked: boolean): void {
    this.inputLocked = locked;
    this.applyControl();
  }
  private setPlayerControl(on: boolean): void {
    this.controlOn = on;
    this.applyControl();
    if (!on) { try { this.playerActor?.stop(); } catch { /* ignore */ } }
  }
  private applyControl(): void {
    const enabled = this.controlOn && !this.inputLocked;
    this.pc?.setInputLocked(!enabled);
    this.playerActor?.setControllable(enabled);
  }

  private confirmHeld(): boolean {
    const k = this.keys;
    const down = (key?: Phaser.Input.Keyboard.Key): boolean => !!key && key.isDown;
    if (down(k['enter']) || down(k['space']) || down(k['z'])) return true;
    if (this.touchHold > 0) return true;
    const p = this.input.activePointer;
    return !!p && p.isDown;
  }
  private dirX(): number {
    const k = this.keys;
    const down = (key?: Phaser.Input.Keyboard.Key): boolean => !!key && key.isDown;
    const x = (down(k['right']) || down(k['d']) ? 1 : 0) - (down(k['left']) || down(k['a']) ? 1 : 0);
    return x !== 0 ? x : this.touchDir;
  }

  // ------------------------------------------------------------------ camera (deterministic)
  private clampScroll(x: number, y: number): Vec2 {
    return { x: Math.round(Math.max(0, Math.min(this.def.width - GAME_W, x))), y: Math.round(Math.max(0, Math.min(this.def.height - GAME_H, y))) };
  }
  cameraPanTo(x: number, y: number, ms: number): Promise<void> {
    const to = this.clampScroll(x - GAME_W / 2, y - GAME_H / 2);
    if (this.pan) { const old = this.pan; this.pan = null; old.resolve(); }
    if (ms <= 0 || this.dead) { this.camMode = 'hold'; this.camHold = to; this.applyCamera(to); return Promise.resolve(); }
    const cam = this.cameras.main;
    const from = { x: Math.round(cam.scrollX - this.shakeNow.x), y: Math.round(cam.scrollY - this.shakeNow.y) };
    return new Promise<void>((resolve) => { this.pan = { from, to, elapsed: 0, duration: ms, resolve }; this.camMode = 'pan'; });
  }
  cameraFollowPlayer(): void {
    if (this.pan) { const old = this.pan; this.pan = null; old.resolve(); }
    this.camMode = 'follow';
  }
  cameraShake(ms: number, intensity: number): void {
    if (ms <= 0 || intensity <= 0) return;
    this.shake = { elapsed: 0, duration: ms, intensity: intensity < 1 ? intensity * GAME_W : intensity };
  }
  private stepCamera(dt: number): void {
    let base: Vec2;
    if (this.camMode === 'pan' && this.pan) {
      const p = this.pan;
      p.elapsed += dt;
      const t = Math.min(1, p.elapsed / p.duration);
      const e = easeInOut(t);
      base = { x: Math.round(p.from.x + (p.to.x - p.from.x) * e), y: Math.round(p.from.y + (p.to.y - p.from.y) * e) };
      if (t >= 1) { this.pan = null; this.camMode = 'hold'; this.camHold = p.to; p.resolve(); }
    } else if (this.camMode === 'follow' && this.playerActor) {
      base = this.clampScroll(this.playerActor.x - GAME_W / 2, this.playerActor.y - 16 - GAME_H / 2);
    } else base = this.camHold;
    if (this.shake) {
      this.shake.elapsed += dt;
      const rem = 1 - this.shake.elapsed / this.shake.duration;
      if (rem <= 0) { this.shake = null; this.shakeNow = { x: 0, y: 0 }; }
      else this.shakeNow = { x: Math.round((this.rng.next() * 2 - 1) * this.shake.intensity * rem), y: Math.round((this.rng.next() * 2 - 1) * this.shake.intensity * rem * 0.6) };
    }
    this.swayT += dt / 1000;
    const swayX = Math.round(this.sway * 5), swayY = Math.round(Math.sin(this.swayT * 2.2) * Math.abs(this.sway) * 3);
    this.applyCamera({ x: base.x + this.shakeNow.x + swayX, y: base.y + this.shakeNow.y + swayY });
  }
  private applyCamera(v: Vec2): void { this.cameras.main.setScroll(Math.round(v.x), Math.round(v.y)); }

  // ------------------------------------------------------------------ HUD
  private buildHud(): void {
    // HUD rows (top-centre, clear of the vignette falloff): title plate 11..31, hint 36..56, meter at 72.
    const hintBg = this.add.rectangle(GAME_W / 2, 46, 10, 20, 0x0b0f14, 0.72).setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI + 1).setAlpha(0);
    const hint = this.add.text(GAME_W / 2, 46, '', { fontFamily: FONT_BODY, fontSize: '12px', color: '#f1e3c2', stroke: '#0b0f14', strokeThickness: 3 }).setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI + 2).setAlpha(0);
    const captionBg = this.add.rectangle(GAME_W / 2, 262, GAME_W - 40, 44, 0x0b0f14, 0.72).setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI + 1).setAlpha(0);
    const caption = this.add.text(GAME_W / 2, 262, '', { fontFamily: FONT_TITLE, fontSize: '14px', color: '#f1e3c2', stroke: '#0b0f14', strokeThickness: 3, align: 'center', wordWrap: { width: GAME_W - 72 } }).setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI + 2).setAlpha(0);
    const meterLabel = this.add.text(GAME_W / 2 - 92, 72, '', { fontFamily: FONT_BODY, fontSize: '11px', color: '#e2c48c', stroke: '#0b0f14', strokeThickness: 3 }).setOrigin(1, 0.5).setScrollFactor(0).setDepth(Depth.UI + 2).setVisible(false);
    const meterBg = this.add.rectangle(GAME_W / 2 - 80, 72, 160, 8, 0x0b0f14, 0.8).setOrigin(0, 0.5).setScrollFactor(0).setDepth(Depth.UI + 1).setVisible(false);
    const meterFill = this.add.rectangle(GAME_W / 2 - 79, 72, 0, 6, 0xffb347, 1).setOrigin(0, 0.5).setScrollFactor(0).setDepth(Depth.UI + 2).setVisible(false);
    this.hud = { hint, hintBg, caption, captionBg, meterLabel, meterBg, meterFill };
  }
  private reduceMotion(): boolean { try { return ctxOf(this)?.settings.reduceMotion ?? false; } catch { return false; } }
  private showHint(text: string, ms = 5000): void {
    const h = this.hud; if (!h || !text) return;
    h.hint.setText(text);
    h.hintBg.setSize(Math.round(h.hint.width) + 16, 20);
    this.tweens.killTweensOf([h.hint, h.hintBg]);
    h.hint.setAlpha(1); h.hintBg.setAlpha(1);
    this.tweens.add({ targets: [h.hint, h.hintBg], alpha: 0, delay: ms, duration: this.reduceMotion() ? 0 : 400 });
  }
  private showCaption(text: string, ms: number): void {
    const h = this.hud; if (!h) return;
    h.caption.setText(text);
    this.tweens.killTweensOf([h.caption, h.captionBg]);
    h.caption.setAlpha(0); h.captionBg.setAlpha(0);
    this.tweens.add({ targets: [h.caption, h.captionBg], alpha: 1, duration: this.reduceMotion() ? 0 : 300 });
    this.tweens.add({ targets: [h.caption, h.captionBg], alpha: 0, delay: ms, duration: this.reduceMotion() ? 0 : 400 });
  }
  private setMeter(label: string | null, value = 0): void {
    const h = this.hud; if (!h) return;
    const on = label !== null;
    h.meterLabel.setVisible(on); h.meterBg.setVisible(on); h.meterFill.setVisible(on);
    if (!on) return;
    h.meterLabel.setText(label);
    h.meterFill.setSize(Math.round(Math.max(0, Math.min(1, value)) * 158), 6);
  }
  private showSpeech(actorId: string, text: string, ms: number): void {
    const actor = this.actors.get(actorId);
    if (!actor) return;
    const t = this.add.text(actor.x, actor.y - 56, text, { fontFamily: FONT_BODY, fontSize: '11px', color: '#ffffff', stroke: '#0b0f14', strokeThickness: 3 }).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + actor.y + 3);
    this.speeches.push({ text: t, actor, remaining: ms });
  }
  private stepSpeeches(dt: number): void {
    for (let i = this.speeches.length - 1; i >= 0; i--) {
      const s = this.speeches[i] as Speech;
      s.remaining -= dt;
      if (s.remaining <= 0) { s.text.destroy(); this.speeches.splice(i, 1); continue; }
      s.text.setPosition(Math.round(s.actor.x), Math.round(s.actor.y - 56)).setDepth(Depth.WORLD_BASE + s.actor.y + 3);
      if (s.remaining < 300) s.text.setAlpha(s.remaining / 300);
    }
  }
}
